import { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Select,
  theme,
  Tooltip as AntdTooltip,
  Switch,
} from "antd";
import { useEffectOnce, useElementSize } from "usehooks-ts";
import {
  BarChartOutlined,
  BarcodeOutlined,
  BlockOutlined,
  BuildOutlined,
  FontSizeOutlined,
  LoadingOutlined,
  MenuOutlined,
  NumberOutlined,
  PauseOutlined,
  PercentageOutlined,
  SwapOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import {
  hierarchy,
  treemap,
  treemapResquarify,
  treemapBinary,
  treemapDice,
  treemapSlice,
  treemapSliceDice,
} from "d3-hierarchy";
import { sum } from "d3-array";
import { nest } from "d3-collection";
import { Flex } from "src/components/Flex";
import { Hover } from "src/components/Hover";
import { DynamicCanvas } from "src/components/DynamicCanvas";
import { FilterMenu } from "src/search/components/inputs/FilterMenu";
import { usePopupContext } from "src/hooks/usePopup";
import { useStickyColors } from "src/hooks/useStickyColors";
import { useSubscribeEffect } from "src/hooks/useSubscribeEffect";
import { cloneData } from "src/utils/objects";
import { asKey, fromKey } from "src/utils/fields";
import {
  attributeToTitle,
  formatValue,
  unitTypeForCol,
} from "src/utils/strings";
import {
  getConsistentColorPalette,
  getContrastTextColor,
  ColorTheme,
} from "src/utils/colors-v2";
import { useColorScheme } from "src/hooks/useColorScheme";
import { InvalidGroupBys } from "./InvalidGroupBys";
import { VisualizationProps } from "./visualization-props";
import { valueFromRecord, valueToLabel, valueToNumber } from "./common";
import { CompareWithConfig, toCompareWithConfig } from "./CompareWithControls";
import { SearchContext } from "src/search/useSearchContext";
import { ContentView, ContentViews } from "./useContentView";
import { EmptyResultExplanation } from "./EmptyResultExplanation";
import { InvalidAggregations } from "./InvalidAggregations";
import { ColorRampConfig, ColorRampPicker } from "./ColorRampPicker";
import { toColorRampConfig } from "src/utils/colors-v2";
import { createPivotCellContext } from "./tooltips/tooltip-types";
import { VZBLTooltip } from "./tooltips/VZBLTooltips";
import { TreeMapCellTooltip } from "./tooltips/TreeMapCellTooltip";
import { TreeMapCellLabel, LabelType } from "./TreeMapCellLabel";

/** How to configure the Tree Map */
type Config = CompareWithConfig &
  ColorRampConfig & {
    labels: boolean;
    labelType: LabelType;
    legend: boolean;
    invertNesting: boolean;
    layout: "binary" | "squarify" | "slice" | "dice" | "slice-dice";
    colorTheme?: string;
    customBaseColor?: string;
  };

interface TreeMapNode {
  id: string;
  depth: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  data: {
    name: string;
    value: number;
    label: string;
    total?: number;
    properties?: any[];
    values?: any[];
    measureValue?: { label: string; value: string };
    ancestors?: { name: string; attribute: string }[];
  };
  parent?: TreeMapNode;
  children?: TreeMapNode[];
}

type ColorFunction = (value?: number) => string;

/** Ensures preconditions are met */
export function TreeMap(props: VisualizationProps) {
  if (
    props.context.params.selectors.countGroupBys(props.context.params.state) ===
      0 ||
    props.context.params.selectors.countGroupBys(props.context.params.state) > 2
  )
    return props.onRefuse ? (
      <Refused onRefuse={props.onRefuse} />
    ) : (
      <InvalidGroupBys
        numGroups={props.context.params.selectors.countGroupBys(
          props.context.params.state
        )}
        requiredGroups={{ min: 1, max: 2 }}
        view={props.context.view}
      />
    );

  if (
    props.context.params.selectors.countAggregations(
      props.context.params.state
    ) > 1
  )
    return props.onRefuse ? (
      <Refused onRefuse={props.onRefuse} />
    ) : (
      <InvalidAggregations
        numAggregations={props.context.params.selectors.countAggregations(
          props.context.params.state
        )}
        view={props.context.view}
        minAggregations={1}
        maxAggregations={1}
      />
    );
  return <SubscribeAndConfigure {...props} />;
}

function Refused({ onRefuse }: { onRefuse(): void }) {
  useEffectOnce(() => onRefuse());
  return null;
}

/**  Loads data and prepares configuration */
function SubscribeAndConfigure(props: VisualizationProps) {
  useEffectOnce(() => {
    props.context.view.setConfig(toConfig(props.context.view.config));
    props.context.aggregations.setWindow({});
  });
  useSubscribeEffect(props.context.aggregations);

  const config = toConfig(props.context.view.config);

  const explanation = props.context.aggregations.data?.meta?.explanation || [];

  return props.context.aggregations.isLoading ? (
    <Flex centered style={{ height: "100%" }}>
      <LoadingOutlined />
    </Flex>
  ) : props.context.aggregations.data?.data?.length === 0 ? (
    <EmptyResultExplanation explanation={explanation} />
  ) : (
    <DynamicCanvas>
      {({ width, height }) => (
        <RenderTreeMap
          {...config}
          {...{ width, height }}
          context={props.context}
        />
      )}
    </DynamicCanvas>
  );
}

/** Render a tree map for the data and config */
function RenderTreeMap(
  props: Config & {
    context: SearchContext & { view: ContentView; title: string };
    width: number;
    height: number;
  }
) {
  const { aggregations, params } = props.context;
  useEffectOnce(() => props.context.aggregations.setWindow({}));
  useSubscribeEffect(aggregations);

  const groupBys = useMemo(
    () => params.state.groupBys || [],
    [params.state.groupBys]
  );

  const isNested = groupBys.length > 1;
  const mode = useColorScheme();
  const [legendPin, setLegendPin] = useState<string | undefined>();
  const [legendHighlight, setLegendHighlight] = useState<string | undefined>();
  const [activeValue, setActiveValue] = useState("");

  // Get colors from the theme using the new color system
  const colors = useMemo(() => {
    // Get the top-level group names for color assignment
    const topLevelGroups = [
      ...new Set(
        (props.context.aggregations.data?.data || [])
          .map((row) => {
            const groupKeys = Object.keys(row).find((key) => key !== "measure");
            return groupKeys ? (row[groupKeys] as string) : "";
          })
          .filter(Boolean)
      ),
    ];

    const colorPalette = getConsistentColorPalette(
      topLevelGroups,
      props.theme,
      mode
    );
    return Object.values(colorPalette);
  }, [props.theme, mode, props.context.aggregations.data?.data]);

  const { token } = theme.useToken();
  const popup = usePopupContext();
  const [legendSizeRef, legendSize] = useElementSize();
  const tree = useTreeMapData({
    ...props,
    height: props.height - legendSize.height,
  });

  // Create a map of top-level sections to colors
  const colorMap = useMemo(() => {
    const nodes = tree as unknown as TreeMapNode[];
    // Get only the direct children of the root node (depth 1)
    const topLevelNodes = nodes.filter((node) => node.depth === 1);

    const map = new Map<string, string>();
    topLevelNodes.forEach((node, index) => {
      const color = colors[index % colors.length];
      map.set(node.data.name, color);
    });

    return map;
  }, [tree, colors]);

  const total = tree.reduce((acc, node) => acc + (node.data.value || 0), 0);

  const values = tree.map((node) => node.data.value || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);

  return (
    <div style={{ height: props.height, overflow: "hidden" }}>
      <div
        style={{
          height: props.height - legendSize.height,
          width: props.width,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {(tree as unknown as TreeMapNode[]).map((node) => {
          if (node.depth === 0) return null; // Don't render root node

          let topLevelNode = node;
          while (topLevelNode.parent && topLevelNode.parent.depth > 0) {
            topLevelNode = topLevelNode.parent;
          }
          const color = colorMap.get(topLevelNode.data.name) || colors[0];

          const isActive =
            activeValue === node.data.name || legendPin === node.data.name;
          const opacity = (activeValue || legendPin) && !isActive ? 0.4 : 1;

          return (
            <div
              key={node.id}
              className="treemap-cell"
              style={{
                position: "absolute",
                left: node.x0,
                top: node.y0,
                width: node.x1 - node.x0,
                height: node.y1 - node.y0,
                backgroundColor: color,
                opacity,
                border: `solid 1px ${token.colorBgContainer}`,
                borderRadius: 2,
                boxSizing: "border-box",
                overflow: "hidden",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onClick={(e) => {
                if (node.data.properties && node.data.values) {
                  popup.setPopup(
                    e,
                    <FilterMenu
                      accentColor={color}
                      context={props.context}
                      properties={node.data.properties}
                      values={node.data.values}
                      measureValue={node.data.measureValue}
                    />
                  );
                }
              }}
            >
              <div
                style={{ width: "100%", height: "100%", position: "relative" }}
              >
                <TreeMapCellTooltip
                  key={node.id}
                  node={node}
                  context={props.context}
                  total={total}
                  min={min}
                  max={max}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      position: "absolute",
                      top: 0,
                      left: 0,
                    }}
                  >
                    {props.labels &&
                    node.y1 - node.y0 > 20 &&
                    node.x1 - node.x0 > 36 ? (
                      <TreeMapCellLabel
                        name={node.data.name}
                        value={node.data.value}
                        displayValue={node.data.label}
                        total={
                          node.parent?.data.total || node.parent?.data.value
                        }
                        color={color}
                        labelType={props.labelType}
                      />
                    ) : (
                      <div style={{ width: "100%", height: "100%" }}>
                        &nbsp;
                      </div>
                    )}
                  </div>
                </TreeMapCellTooltip>
              </div>
            </div>
          );
        })}
      </div>
      <div
        ref={legendSizeRef}
        style={{
          display: "flex",
          gap: "4px 24px",
          flexWrap: "wrap",
          paddingTop: props.legend ? 8 : 0,
        }}
      >
        {props.legend && (
          <div
            style={{
              height: 32,
              overflow: "hidden",
              backgroundColor: token.colorBgContainer,
              borderRadius: 2,
              padding: "2px 4px",
              width: "100%",
            }}
            onMouseLeave={() => {
              if (!legendPin) {
                setLegendHighlight(undefined);
                setActiveValue("");
              }
            }}
          >
            <Flex
              start
              wrap
              style={{
                gap: "1px 4px",
                maxWidth: "100%",
                justifyContent: "center",
                minHeight: 28,
              }}
              onMouseMove={(e) => e.stopPropagation()}
              onClick={() => {
                setLegendHighlight(undefined);
                setLegendPin(undefined);
                setActiveValue("");
              }}
            >
              {[...colorMap.entries()].map(([name, color]) => (
                <div
                  key={name}
                  onMouseMove={(e) => {
                    if (!legendPin) {
                      e.stopPropagation();
                      setLegendHighlight(name);
                      setActiveValue(name);
                    }
                  }}
                  onMouseOut={(e) => {
                    e.stopPropagation();
                    setLegendHighlight((val) => (val === undefined ? val : ""));
                    if (!legendPin) {
                      setActiveValue("");
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLegendHighlight(undefined);
                    setLegendPin(name);
                    setActiveValue(name);
                  }}
                  style={{
                    alignItems: "center",
                    alignSelf: "center",
                    backgroundColor: [legendPin, legendHighlight].includes(name)
                      ? token.colorPrimary
                      : "transparent",
                    cursor: "default",
                    borderRadius: 2,
                    color: [legendPin, legendHighlight].includes(name)
                      ? token.colorTextLightSolid
                      : token.colorTextSecondary,
                    display: "inline-flex",
                    fontSize: 11,
                    gap: 3,
                    height: 14,
                    maxWidth: 200,
                    minWidth: 50,
                    overflow: "hidden",
                    padding: "0px 3px",
                    textAlign: "left",
                    textOverflow: "ellipsis",
                    verticalAlign: "top",
                    whiteSpace: "nowrap",
                  }}
                >
                  <div
                    style={{
                      height: 7,
                      width: 7,
                      marginLeft: 2,
                      backgroundColor: [legendPin, legendHighlight].includes(
                        name
                      )
                        ? token.colorTextLightSolid
                        : color,
                      borderRadius: 4,
                    }}
                  />
                  <div className="trunc">{valueToLabel(name)}</div>
                </div>
              ))}
              {legendPin && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setLegendPin(undefined);
                    setLegendHighlight(undefined);
                    setActiveValue("");
                  }}
                  type="text"
                  shape="circle"
                  size="small"
                  icon={<ClearOutlined />}
                  style={{
                    marginTop: 0,
                    marginLeft: -4,
                    height: 14,
                    width: 14,
                  }}
                />
              )}
            </Flex>
          </div>
        )}
      </div>
    </div>
  );
}

/** For explore sidebar and dashboard card builder */
export function TreeMapControls(props: {
  context: SearchContext & { view: ContentView; title: string };
}) {
  const { params, view } = props.context;
  const config = toConfig(view.config);
  const disabled = params.selectors.countGroupBys(params.state) === 0;
  const hasCompareWith = params.state.compareWith?.method === "tuple";
  const { token } = theme.useToken();
  return (
    <Flex direction="column">
      <div className="section">
        <div className="section-header">Layout</div>
        {/* Layout */}
        <div style={{ marginBottom: 16 }}>
          <div className="layout-control-grid">
            <Button
              type="default"
              onClick={() => view.setConfig({ ...config, layout: "squarify" })}
              className={`layout-control-button ${
                config.layout === "squarify" ? "selected" : ""
              }`}
            >
              <BlockOutlined />
              <span className="layout-control-button-label">Square</span>
            </Button>
            <Button
              type="default"
              onClick={() => view.setConfig({ ...config, layout: "binary" })}
              className={`layout-control-button ${
                config.layout === "binary" ? "selected" : ""
              }`}
            >
              <BuildOutlined />
              <span className="layout-control-button-label">Binary</span>
            </Button>
            <Button
              type="default"
              onClick={() => view.setConfig({ ...config, layout: "slice" })}
              className={`layout-control-button ${
                config.layout === "slice" ? "selected" : ""
              }`}
            >
              <MenuOutlined />
              <span className="layout-control-button-label">Slice</span>
            </Button>
            <Button
              type="default"
              onClick={() => view.setConfig({ ...config, layout: "dice" })}
              className={`layout-control-button ${
                config.layout === "dice" ? "selected" : ""
              }`}
            >
              <PauseOutlined />
              <span className="layout-control-button-label">Dice</span>
            </Button>
            <Button
              type="default"
              onClick={() =>
                view.setConfig({ ...config, layout: "slice-dice" })
              }
              className={`layout-control-button ${
                config.layout === "slice-dice" ? "selected" : ""
              }`}
            >
              <BarcodeOutlined />
              <span className="layout-control-button-label">Slice & Dice</span>
            </Button>
          </div>
          <Flex spaceBetween style={{ width: "100%" }}>
            <span
              style={{
                fontSize: "14px",
                color: token.colorText,
              }}
            >
              <SwapOutlined /> Invert Axis
            </span>
            <Switch
              checked={config.invertNesting}
              onChange={(checked) =>
                view.setConfig({ ...config, invertNesting: checked })
              }
              size="small"
            />
          </Flex>
        </div>
      </div>

      <div className="section">
        <div className="section-header">Style</div>
        <div className="layout-control-grid">
          <AntdTooltip title="Show Labels">
            <Button
              type="default"
              onClick={() =>
                view.setConfig({ ...config, labels: !config.labels })
              }
              className={`layout-control-button ${
                config.labels ? "selected" : ""
              }`}
            >
              <FontSizeOutlined />
              <span className="layout-control-button-label">Labels</span>
            </Button>
          </AntdTooltip>
          <AntdTooltip title="Show Legend">
            <Button
              type="default"
              onClick={() =>
                view.setConfig({ ...config, legend: !config.legend })
              }
              className={`layout-control-button ${
                config.legend ? "selected" : ""
              }`}
            >
              <svg
                className="layout-control-button-icon"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="4"
                  y="6"
                  width="3"
                  height="3"
                  rx="0.5"
                  fill="currentColor"
                />
                <rect
                  x="4"
                  y="11"
                  width="3"
                  height="3"
                  rx="0.5"
                  fill="currentColor"
                />
                <rect
                  x="4"
                  y="16"
                  width="3"
                  height="3"
                  rx="0.5"
                  fill="currentColor"
                />
                <path
                  d="M9 7.5h11M9 12.5h11M9 17.5h11"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span className="layout-control-button-label">Legend</span>
            </Button>
          </AntdTooltip>
        </div>
        <ColorRampPicker
          ramp={config.theme}
          onChangeRamp={(value) => view.setConfig({ ...config, theme: value })}
        />
        {config.labels && (
          <Select
            value={config.labelType}
            onChange={(value) =>
              view.setConfig({ ...config, labelType: value })
            }
            style={{ width: "100%" }}
            options={[
              { label: "Name", value: "name" },
              { label: "Value", value: "value" },
              { label: "Percentage", value: "percentage" },
              { label: "Name & Value", value: "name-value" },
              { label: "Name & Percentage", value: "name-percentage" },
            ]}
          />
        )}
      </div>
    </Flex>
  );
}

/** Creates the primary data structure for rendering a tree map */
function useTreeMapData({
  context,
  height,
  invertNesting,
  layout,
  showChangeAs,
  width,
}: Pick<Config, "invertNesting" | "layout" | "showChangeAs"> & {
  context: SearchContext & { view: ContentView; title: string };
  width: number;
  height: number;
}) {
  const { aggregations, params } = context;
  const hasCompareWith = params.state.compareWith?.method === "tuple";
  const asPercent = showChangeAs === "percentage";

  const groupBys = useMemo(
    () => params.state.groupBys || [],
    [params.state.groupBys]
  );

  // Create the hierarchical structure
  const keys = Object.keys((aggregations.data?.data || [])[0] || {});
  const lookup = keys.at(-1);

  const columnInfo = aggregations.data?.meta?.columnInfo;
  const aggregation =
    context.params.state.aggregations?.at(0) ||
    columnInfo?.find((c) => c.reducer);

  return useMemo(() => {
    // Log the raw input data

    const zType = (aggregation
      ? columnInfo?.find((c) => asKey(c) == asKey(aggregation))
      : { type: "string", unit: undefined }) || {
      type: "string",
      unit: undefined,
    };

    type RecursiveHierarchy = {
      value?: number;
      total?: number;
      name: string;
      label?: string;
      attribute?: string;
      ancestors: { name: string; attribute?: string }[];
      children?: RecursiveHierarchy[];
      properties?: { target: string; attribute: string }[];
      values?: { value: string; label: string }[];
      measureValue?: { label: string; value: string };
    };

    const flatData = (aggregations.data?.data || []).map((row) => {
      const ret: Record<string, string | number> = {};
      Object.keys(row).forEach((k) => {
        const { delta, percent, value } = valueFromRecord(row, k, columnInfo);
        ret[k] =
          hasCompareWith && delta !== undefined && percent !== undefined
            ? Math.abs(Number(asPercent ? percent : delta)) + ""
            : value;
      });

      return ret;
    });

    const orderedGroupBys = groupBys.map((group) => asKey(group));

    if (invertNesting) orderedGroupBys.reverse();

    function createHierarchyRecursive(
      data: Record<string, string | number>[],
      properties: string[],
      name: string,
      attribute?: string,
      ancestors: { name: string; attribute?: string }[] = []
    ): RecursiveHierarchy {
      if (data.length === 0) return { name, children: [], ancestors };

      const [property, ...siblingProperties] = properties;

      const variations = nest()
        .key((data_: unknown) => (data_ as Record<string, string>)[property])
        .entries(cloneData(data));

      const children = variations.map(({ key: childKey, values }) => {
        const trimmed = values.map((v: Record<string, string | number>) => {
          delete v[name];
          return v;
        });

        const colType = unitTypeForCol(property, columnInfo!) || {
          type: "string",
          unit: undefined,
          tuple: false,
        };
        const colLabel = formatValue(childKey, colType.type, colType.unit);

        // Calculate the sum for this node
        const nodeValue = sum(
          trimmed,
          (v: Record<string, number>) => v[lookup!]
        );

        // Prepare the properties and values for click handling
        const nodeProperties = [{ target: property, attribute: property }];
        const nodeValues = [{ value: childKey, label: colLabel }];

        return siblingProperties.length > 0
          ? createHierarchyRecursive(
              trimmed,
              siblingProperties,
              colLabel,
              property,
              ancestors
            )
          : {
              name: colLabel,
              attribute: property,
              value: nodeValue,
              label: formatValue(nodeValue, zType!.type, zType!.unit),
              ancestors: [...ancestors, { name, attribute }],
              // Add these properties for click handling
              properties: nodeProperties,
              values: nodeValues,
              measureValue: {
                label: lookup || "",
                value: nodeValue.toString(),
              },
            };
      });

      return {
        name,
        label: formatValue(
          children.reduce((a, c) => a + (c.value || 0), 0),
          zType!.type,
          zType!.unit
        ),
        total: children.reduce((a, c) => a + (c.value || 0), 0),
        attribute,
        children,
        ancestors,
        // Add these properties for click handling at parent level too
        properties: attribute ? [{ target: attribute, attribute }] : undefined,
        values: attribute ? [{ value: name, label: name }] : undefined,
        measureValue: {
          label: lookup || "",
          value: children.reduce((a, c) => a + (c.value || 0), 0).toString(),
        },
      };
    }

    const hierarchyRoot = createHierarchyRecursive(
      flatData,
      orderedGroupBys,
      "Total"
    );

    const root = hierarchy(hierarchyRoot)
      .sum(({ value }) => value || 0)
      .sort(({ value: a }, { value: b }) => (b || 0) - (a || 0));

    const arranger = treemap()
      .size([width, height])
      .tile(
        layout === "binary"
          ? treemapBinary
          : layout === "squarify"
          ? treemapResquarify
          : layout === "slice"
          ? treemapSlice
          : layout === "dice"
          ? treemapDice
          : treemapSliceDice
      )
      .paddingInner(2);

    // @ts-ignore 😶
    arranger(root);

    const keySet = new Set<string>();
    return root
      .descendants()
      .map((node) => {
        const treeNode = node as unknown as TreeMapNode;
        const ancestors = node.ancestors().slice(1); // Skip root node
        return {
          ...treeNode,
          key: `${treeNode.depth}_${ancestors
            .map((n) => (n as any).data.name)
            .join("_")}_${treeNode.data.name}_${treeNode.x0}_${treeNode.x1}_${
            treeNode.y0
          }_${treeNode.y1}`,
        };
      })
      .filter((item) => {
        if (!keySet.has(item.key)) {
          keySet.add(item.key);
          return true;
        }
        return false;
      })
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [
    asPercent,
    hasCompareWith,
    aggregation,
    aggregations.data?.data,
    columnInfo,
    groupBys,
    height,
    invertNesting,
    layout,
    lookup,
    width,
  ]);
}

function toConfig(data: Config | any): Config {
  return {
    ...toCompareWithConfig(data),
    ...toColorRampConfig(data),
    labels: typeof data?.labels === "boolean" ? data.labels : true,
    labelType: [
      "name",
      "value",
      "percentage",
      "name-value",
      "name-percentage",
    ].includes(data?.labelType)
      ? data.labelType
      : "name",
    legend: typeof data?.legend === "boolean" ? data.legend : true,
    invertNesting:
      typeof data?.invertNesting === "boolean" ? data.invertNesting : false,
    layout: ["binary", "squarify", "slice", "dice", "slice-dice"].includes(
      data?.layout
    )
      ? data.layout
      : "squarify",
  };
}
